import { useEffect, useState } from 'react';
import { SwiperClass } from 'swiper/react';

import { BreakPointHooks } from 'Scripts/Hooks/BreakPointHooks';

import { IBannerSliderBlockViewModel } from 'Types/Litium';

import CaretIcon from 'Icons/caretGsd.svg?react';

const { useGreater } = BreakPointHooks();

interface ISliderBlockNavigation {
    slides: IBannerSliderBlockViewModel['sliders'];
    realIndex: number;
    swiper: SwiperClass;
    translationNextSlide: string;
    translationPreviousSlide: string;
    itemsToShowDesktop?: number;
    itemsToShowMobile?: number;
}

const SliderBlockNavigation = ({
    slides,
    realIndex,
    swiper,
    translationNextSlide,
    translationPreviousSlide,
    itemsToShowDesktop = 1,
    itemsToShowMobile = 1,
}: ISliderBlockNavigation) => {
    const isGreater = useGreater('lg');
    const arrowPosition = () =>
        slides[realIndex]?.sliderBlockTextPosition === 'bottom' ||
        slides[realIndex]?.sliderBlockTextPosition === null ||
        slides[realIndex]?.sliderBlockTextPosition === undefined
            ? 'top-[calc(var(--imageHeight)-12px)] lg:top-[calc(var(--imageHeight)-16px)]'
            : 'bottom-[calc(var(--imageHeight)-12px)] lg:bottom-[calc(var(--imageHeight)-16px)]';

    const isArrowsHidden = () =>
        `${slides.length <= itemsToShowMobile ? 'hidden' : 'flex'} ${
            slides.length <= itemsToShowDesktop ? 'lg:hidden' : 'lg:flex'
        }`;

    const [
        baseImageHeightBasedOnSwiperWidth,
        setBaseImageHeightBasedOnSwiperWidth,
    ] = useState<string>(null);

    const gotoPrev = () => {
        swiper.slidePrev(600);
    };

    const gotoNext = () => {
        swiper.slideNext(600);
    };

    useEffect(() => {
        const handleResize = () => {
            const activeSlide = slides[realIndex];
            if (activeSlide) {
                const baseImageHeight =
                    !isGreater && activeSlide.imageMobile?.src
                        ? activeSlide.imageMobile?.height ?? 0
                        : activeSlide.image?.height ?? 0;
                const baseImageWidth =
                    !isGreater && activeSlide.imageMobile?.src
                        ? activeSlide.imageMobile.width ?? 0
                        : activeSlide.image?.width ?? 0;
                const aspectRatio = baseImageHeight / baseImageWidth;
                const slideMediaHeight =
                    (swiper?.slides?.[realIndex]?.clientWidth ?? 0) *
                    aspectRatio;
                setBaseImageHeightBasedOnSwiperWidth(
                    `${
                        slideMediaHeight > 0
                            ? `${slideMediaHeight / 2}px`
                            : '50%'
                    }`
                );
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [realIndex]);

    return (
        <>
            <button
                onClick={gotoPrev}
                style={
                    {
                        '--imageHeight': baseImageHeightBasedOnSwiperWidth,
                    } as React.CSSProperties
                }
                className={`absolute left-1 z-[1] flex h-8 w-8 items-center justify-center rounded-full bg-silverLight lg:left-2.5 ${arrowPosition()} ${isArrowsHidden()}`}
            >
                <CaretIcon className="h-4 w-4 rotate-180" />
                <span className="sr-only">{translationPreviousSlide}</span>
            </button>
            <button
                onClick={gotoNext}
                style={
                    {
                        '--imageHeight': baseImageHeightBasedOnSwiperWidth,
                    } as React.CSSProperties
                }
                className={`absolute right-1 z-[1] flex h-8 w-8 items-center justify-center rounded-full bg-silverLight lg:right-2.5 ${arrowPosition()} ${isArrowsHidden()}`}
            >
                <CaretIcon className="h-4 w-4" />
                <span className="sr-only">{translationNextSlide}</span>
            </button>
        </>
    );
};

export default SliderBlockNavigation;
